import { useLayoutEffect } from 'react'
import patternClosure from './patterns.js'
import { colors } from '#Theme'
// initially non-animated path gradient patterns
const { light, primary, lightSpark, primarySpark } = colors

let animationFrameRequested, timer, waiting4 = null;

export default function useFiligree(platform, dimensions, paths, bgCanvas) {
    useLayoutEffect(() => {
        // console.log('filigree hook', dimensions, bgCanvas.current)
        //originally, was only using dimension update to trigger second call to this which actually drew initial filigree
        //later, when I changed dimensions to useReducer and didn't wait for mainBox to mount, had to wait for bgCanvas.current to mount before I could draw
        if(!dimensions || !bgCanvas.current) return
        drawFiligree(platform, dimensions, paths, bgCanvas.current)
    }, [dimensions, bgCanvas.current])
}

function drawFiligree(platform, dimensions, paths, canvas) {
    // console.log('drawFiligree')
    let { w0, h0, w, h } = dimensions;
    let halfUnit = platform === 'desktop' ? Math.floor(w0 / 200) : Math.floor(h0 / 200);
    cancelTransitions()
    canvas.width = w0;
    canvas.height = h0;
    let c = canvas.getContext('2d')
    let lineW1 = (platform === 'mobile' ? 10 : 16)
    let lineW2 = (platform === 'mobile' ? 18 : 24)
    c.lineWidth = lineW1;
    c.translate(0.5, 0.5)
    let { p1, p1b, p1c, p1d, p2, p2b, p2c, x1, x2, x3, x4, x5, x6, x7, x8, x9, x10 } = paths;

    let patterns = [];
    const PathPattern = patternClosure(patterns, c, w, h, w0);
    patterns[0] = new PathPattern(p1, primary, lightSpark, 3, .0015);
    patterns[1] = new PathPattern(p1b, primary, lightSpark, null, .005);
    patterns[2] = new PathPattern(p1c, primary, lightSpark, null, .005);
    patterns[3] = new PathPattern(p2, light, primarySpark, 0, .0015);
    patterns[4] = new PathPattern(p2b, light, primarySpark, null, .004);
    patterns[5] = new PathPattern(p2c, light, primarySpark, null, .004);
    patterns[0].start()

    draw(platform)

    function draw() {
        if (waiting4) {
            patterns[waiting4.path].start()
            waiting4 = null;
        }
        c.clearRect(0, 0, w0, h0)
        for (let p of patterns) {
            if (p.active) {
                let update = p.update()
                if (update && !waiting4) waiting4 = update;
            }
        }
        drawAllPaths()

        // //animate patterns
        if (!waiting4) {
            animationFrameRequested = window.requestAnimationFrame(draw)
        }
        else {
            timer = setTimeout(draw, 3000)
        }

        //Helper methods

        function drawAllPaths() {
            //path segments
            drawPath(p1c, patterns[2].display, (platform === 'desktop' ? -8 : 0))
            drawPath(p1, patterns[0].display)
            //p1b drawn after crossings
            drawPath(p2b, patterns[4].display, 5)
            drawPath(p2c, patterns[5].display, 8, halfUnit / 2)
            drawPath(p2, patterns[3].display)

            //crossing segments
            drawCrossing(x1, patterns[0].display)
            drawCrossing(x2, patterns[3].display)
            drawCrossing(x3, patterns[0].display)
            drawCrossing(x4, patterns[3].display)

            drawCrossing(x6, patterns[3].display)
            drawCrossing(x7, patterns[0].display)
            drawCrossing(x8, patterns[5].display, 8)
            drawCrossing(x9, patterns[0].display)
            drawCrossing(x10, patterns[3].display)

            //kind of tricky crossing, required some custom hard-coding
            drawCrossing(x5, patterns[1].display, 0, 'destination-over')
            drawPath(p1b, patterns[1].display)
            c.globalCompositeOperation = 'source-over'
            drawPath(p1d, patterns[0].display)
        }

        function drawPath(path, style, offset = 0, startOffset = 0) {
            c.strokeStyle = style;
            c.beginPath()
            c.lineCap = 'square';
            let xOffset = (platform === 'desktop' ? offset : 0);
            let yOffset = (platform !== 'desktop' ? offset : 0);
            c.moveTo(w[path[0][0]] + xOffset + (platform === 'desktop' ? startOffset : 0), h[path[0][1]] + yOffset + (platform !== 'desktop' ? startOffset : 0))
            for (let i = 1; i < path.length; i++) {
                c.lineTo(w[path[i][0]] + xOffset, h[path[i][1]] + yOffset)
            }
            c.stroke()
            c.closePath()
        }

        function drawCrossing(path, style, offset = 0, composite = 'source-over') {
            c.strokeStyle = style;
            c.lineCap = 'butt'
            //first pass over path clears underneath
            c.globalCompositeOperation = 'destination-out';
            c.lineWidth = lineW2;
            c.beginPath()
            let xOffset = (platform === 'desktop' ? offset : 0);
            let yOffset = (platform !== 'desktop' ? offset : 0);
            c.moveTo(w[path[0][0]] + xOffset + (platform === 'desktop' && path[0][2] ? halfUnit : 0), h[path[0][1]] + yOffset + (platform !== 'desktop' && path[0][2] ? halfUnit : 0))
            for (let i = 1; i < path.length; i++) {
                c.lineTo(w[path[i][0]] + xOffset, h[path[i][1]] + yOffset)
            }
            c.stroke();
            c.closePath()
            //second pass draws the actual overlapping path segment
            c.lineCap = 'square'
            c.globalCompositeOperation = composite;
            c.lineWidth = lineW1;
            c.beginPath()
            c.moveTo(w[path[0][0]] + xOffset + (platform === 'desktop' && path[0][2] ? halfUnit : 0), h[path[0][1]] + yOffset + (platform !== 'desktop' && path[0][2] ? halfUnit : 0))
            for (let i = 1; i < path.length; i++) {
                c.lineTo(w[path[i][0]] + xOffset, h[path[i][1]] + yOffset)
            }
            c.stroke();
            c.closePath()
        }
    }
}

function cancelTransitions() {
    if (animationFrameRequested) {
        window.cancelAnimationFrame(animationFrameRequested);
        animationFrameRequested = null;
    }
    if (timer) {
        clearTimeout(timer);
        timer = null;
    }
}